import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Users, MapPin, Check, Minus, Info, Ticket, Zap, Languages, Sun, ShieldCheck } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { api, tr } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BookingWidget } from "@/components/tours/BookingWidget";
import { Reveal } from "@/components/site/Reveal";
import { nearestTown } from "@/lib/geo";
import { basePriceEuro, durationLabel, participantsLabel, priceUnitLabel, isPerGroup, LANG_NAMES } from "@/lib/pricing";

const Eyebrow = ({ children }) => (
  <div className="mb-6">
    <p className="text-xs font-medium tracking-super-wide uppercase text-sea">{children}</p>
    <div className="brand-line mt-3" />
  </div>
);

export default function TourDetailPage() {
  const { slug } = useParams();
  const { t, lang, settings } = useSite();
  const [tour, setTour] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/tours/${slug}`).then((r) => { setTour(r.data); setState("ok"); }).catch(() => setState("error"));
    window.scrollTo(0, 0);
  }, [slug]);

  if (state === "loading") {
    return <div className="pt-40 pb-32 text-center text-ocean/50" data-testid="tour-loading"><div className="w-10 h-10 mx-auto border-2 border-sea border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (state === "error") {
    return (
      <div className="pt-40 pb-32 px-6 text-center" data-testid="tour-not-found">
        <p className="text-ocean/70">{t("detail.notFound")}</p>
        <Link to="/tours" className="inline-block mt-6 px-6 py-3 bg-ocean text-ivory text-sm uppercase tracking-wider rounded-xl">{t("detail.back")}</Link>
      </div>
    );
  }

  const c = tr(tour, lang);
  const town = nearestTown(tour.lat, tour.lng);
  const perGroup = isPerGroup(tour);
  const meetingPending = tour.meeting_point_status !== "confirmed";
  const ticketKey = { yes: "ticketYes", no: "ticketNo", partial: "ticketPartial" }[tour.ticket_included] || "ticketUnconfirmed";
  const langs = (tour.languages || []).map((l) => LANG_NAMES[l] || l.toUpperCase()).join(" · ");
  const policy = settings.cancellation_policy?.[lang] || settings.cancellation_policy?.en;
  const facts = [
    { icon: Clock, l: t("detail.durationL"), v: durationLabel(tour, t), id: "duration" },
    { icon: Users, l: t("facts.participants"), v: participantsLabel(tour, t), id: "participants" },
    { icon: MapPin, l: t("detail.meeting"), v: tour.meeting_point, sub: meetingPending ? t("facts.meetingPending") : null, id: "meeting" },
    { icon: Ticket, l: t("facts.ticket"), v: t(`facts.${ticketKey}`), id: "ticket" },
    { icon: Languages, l: t("facts.languages"), v: langs, id: "languages" },
    { icon: Sun, l: t("facts.start"), v: tour.timeslots?.length ? tour.timeslots.join(" · ") : t("facts.startChoice"), id: "start" },
  ];

  return (
    <div data-testid="tour-detail-page">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] flex items-end overflow-hidden" data-testid="tour-hero">
        <img src={tour.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative max-w-6xl mx-auto w-full px-6 pb-14">
          <Link to="/tours" data-testid="tour-back-link" className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] uppercase text-ivory/85 hover:text-sand-brand" style={{ transition: "color .2s ease" }}><ArrowLeft size={14} />{t("detail.back")}</Link>
          <Reveal className="mt-6">
            <p className="text-xs font-medium tracking-super-wide text-sand-brand uppercase mb-4">{t(`cat.${tour.category}`)} · {town} · {t(`type.${tour.tour_type || "group"}`)}</p>
            <h1 className="font-serif text-4xl md:text-6xl text-ivory font-normal leading-tight max-w-4xl" data-testid="tour-title">{c.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ivory/85">
              <span className="inline-flex items-center gap-2"><Clock size={15} className="text-sand-brand" />{durationLabel(tour, t)}</span>
              <span className="inline-flex items-center gap-2"><Users size={15} className="text-sand-brand" />{participantsLabel(tour, t)}</span>
              {tour.skip_the_line === "yes" && <span className="inline-flex items-center gap-2" data-testid="tour-hero-skipline"><Zap size={15} className="text-sand-brand" />{t("facts.skipLineYes")}</span>}
              <span className="inline-flex items-center gap-2" data-testid="tour-hero-price">{!perGroup && t("card.from")} <span className="font-serif text-2xl text-ivory">{basePriceEuro(tour)}</span> {priceUnitLabel(tour, t)}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-16 md:py-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 space-y-16">
            <Reveal>
              <p className="font-serif text-2xl md:text-3xl text-ocean leading-snug">{c.short}</p>
            </Reveal>

            {/* Quick facts */}
            <Reveal className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {facts.map(({ icon: Icon, l, v, sub, id }) => (
                <div key={id} className="bg-white rounded-2xl p-5 border border-sand/70 shadow-soft" data-testid={`tour-fact-${id}`}>
                  <div className="w-9 h-9 rounded-full bg-sage-mist flex items-center justify-center"><Icon size={16} className="text-sage-dark" /></div>
                  <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-ocean/50">{l}</p>
                  <p className="mt-1 text-sm text-ocean font-medium">{v}</p>
                  {sub && <p className="mt-1 text-xs text-ocean/55 leading-snug">{sub}</p>}
                </div>
              ))}
            </Reveal>

            <Reveal>
              <Eyebrow>{t("detail.experience")}</Eyebrow>
              {c.description.split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="text-base md:text-lg text-ocean/75 leading-relaxed mb-5">{p}</p>
              ))}
            </Reveal>

            {tour.gallery?.length > 0 && (
              <Reveal className="grid grid-cols-2 gap-4">
                {[tour.image, ...tour.gallery].slice(0, 4).map((src, i) => (
                  <div key={i} className={`img-zoom rounded-2xl overflow-hidden ${i === 0 ? "col-span-2 aspect-[16/8]" : "aspect-[4/3]"}`}>
                    <img src={src} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                ))}
              </Reveal>
            )}

            {c.itinerary?.length > 0 && (
              <Reveal>
                <Eyebrow>{t("detail.yourDay")}</Eyebrow>
                <ol className="relative border-l border-sage-light ml-2 space-y-8" data-testid="tour-itinerary">
                  {c.itinerary.map((s, i) => (
                    <li key={i} className="pl-8 relative">
                      <span className="absolute -left-[6px] top-1.5 w-[11px] h-[11px] rounded-full bg-ivory border-2 border-sea" />
                      <p className="text-xs font-medium tracking-[0.18em] uppercase text-sage-dark">{s.time}</p>
                      <h4 className="font-serif text-xl text-ocean mt-1">{s.title}</h4>
                      <p className="text-sm text-ocean/65 mt-1 leading-relaxed">{s.text}</p>
                    </li>
                  ))}
                </ol>
              </Reveal>
            )}

            <Reveal className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-7 border border-sand/70 shadow-soft">
                <p className="text-xs font-medium tracking-super-wide uppercase text-sea mb-5">{t("detail.included")}</p>
                <ul className="space-y-3" data-testid="tour-included">
                  {c.included.map((x, i) => (<li key={i} className="flex gap-3 text-sm text-ocean/80"><Check size={16} className="text-sage-dark mt-0.5 shrink-0" />{x}</li>))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-7 border border-sand/70 shadow-soft">
                <p className="text-xs font-medium tracking-super-wide uppercase text-ocean/50 mb-5">{t("detail.notIncluded")}</p>
                <ul className="space-y-3" data-testid="tour-not-included">
                  {c.not_included.map((x, i) => (<li key={i} className="flex gap-3 text-sm text-ocean/65"><Minus size={16} className="text-ocean/35 mt-0.5 shrink-0" />{x}</li>))}
                </ul>
              </div>
            </Reveal>

            {c.good_to_know?.length > 0 && (
              <Reveal>
                <Eyebrow>{t("detail.goodToKnow")}</Eyebrow>
                <ul className="space-y-3">
                  {c.good_to_know.map((x, i) => (<li key={i} className="flex gap-3 text-sm text-ocean/75"><Info size={16} className="text-sea mt-0.5 shrink-0" />{x}</li>))}
                </ul>
              </Reveal>
            )}

            {policy && (
              <Reveal>
                <Eyebrow>{t("booking.cancellation")}</Eyebrow>
                <p className="flex gap-3 text-sm text-ocean/75 leading-relaxed" data-testid="tour-cancellation-policy"><ShieldCheck size={16} className="text-sage-dark mt-0.5 shrink-0" />{policy}</p>
              </Reveal>
            )}

            {c.faqs?.length > 0 && (
              <Reveal>
                <Eyebrow>{t("detail.faq")}</Eyebrow>
                <Accordion type="single" collapsible className="bg-white rounded-2xl px-6 border border-sand/70 shadow-soft">
                  {c.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`} className={i === c.faqs.length - 1 ? "border-b-0" : "border-sand"}>
                      <AccordionTrigger className="font-serif text-lg text-ocean text-left hover:no-underline" data-testid={`tour-faq-${i}`}>{f.q}</AccordionTrigger>
                      <AccordionContent className="text-sm text-ocean/70 leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28" id="book">
              <BookingWidget tour={tour} title={c.title} />
              <p className="mt-4 text-center text-xs text-ocean/50">{t("detail.reply")}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
